import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes with clsx
 * Used by shadcn/ui components
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * `truncate`, with room for the glyphs it would otherwise shear.
 *
 * 🪤 `truncate` is `overflow: hidden`, and that clips at the **padding box** — a box sized from the
 * font's *advance widths* and *line-height*, neither of which bounds where the ink actually lands. Two
 * ways text escapes it, and italic text does both:
 *
 * * **Horizontally** — an italic glyph leans right, so the LAST character's ink overhangs its advance
 *   width and loses its right edge. At a ~12° slant on 12px text that is roughly 2.5px of the letter.
 * * **Vertically** — ascenders, descenders and `/` routinely exceed a tight line box.
 *
 * The giveaway is that it happens to strings that are **not truncated at all**: reported 2026-09-22 on
 * two picklist remarks of 15 and 27 characters, both fully on screen, no ellipsis in sight. So it reads
 * as a rendering fault rather than as truncation, and chasing `text-overflow` finds nothing.
 *
 * `p-1` pushes the clip box 4px out on every side; `-m-1` takes the same 4px back out of the layout, so
 * nothing moves. Use this anywhere `truncate` meets italic or descender-heavy text.
 */
export const TRUNCATE_SAFE = 'truncate p-1 -m-1';

/**
 * The overflow test behind `titleIfClamped`, on plain numbers so it can be tested without a DOM. More than a
 * pixel of slack either way, because a fractional layout can leave the two a pixel apart with nothing cut.
 */
export function isClamped(el: Pick<HTMLElement, 'scrollHeight' | 'clientHeight' | 'scrollWidth' | 'clientWidth'>): boolean {
  return el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1;
}

/**
 * PT-7 G3 — a native `title` only when the text is actually cut (UI-GUIDELINES §8: anything we knowingly clip
 * owes the full string on hover). Call it from the clamped element's wrapper on pointer enter, with the clamped
 * element itself. It writes the DOM attribute directly — no React state, so a hover re-renders nothing — and
 * React must not own `title` on that element. A text that fits gets no title, so no redundant popup.
 */
export function titleIfClamped(el: HTMLElement | null, text: string): void {
  if (!el) return;
  if (isClamped(el)) el.setAttribute('title', text);
  else el.removeAttribute('title');
}
